/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  vault: {
    all: ["vault"] as const,
    /** Fila raíz de cuenta (existe setup local?). Scoped por userId. */
    account: (userId: string | undefined) => ["vault", "account", userId] as const,
    /** Items vivos de un vault. */
    items: (vaultId: string | undefined) => ["vault", "items", vaultId] as const,
    /** Adjuntos cifrados vivos de un item. */
    attachments: (itemId: string | undefined) => ["vault", "attachments", itemId] as const,
    /** Items de un vault para el análisis de salud (clave distinta al listado). */
    health: (vaultId: string | undefined) => ["vault", "health", vaultId] as const,
    /** Vaults de organización visibles (el sync ya los limita a mis orgs). */
    orgVaults: (userId: string | undefined) => ["vault", "orgVaults", userId] as const,
    /** MI membresía (clave sellada) de un vault de organización. */
    membership: (vaultId: string | undefined, userId: string | undefined) =>
      ["vault", "membership", vaultId, userId] as const,
    /** Secretos compartidos conmigo (sobres sellados a mi clave pública). */
    shares: (userId: string | undefined) => ["vault", "shares", userId] as const,
    /** Claves públicas de los miembros de una org (REST, no PowerSync). */
    memberKeys: (orgId: string | undefined) => ["vault", "memberKeys", orgId] as const,
    /** Organizaciones locales (sync) para los flujos del vault, por pantalla. */
    localOrgs: (userId: string | undefined, scope: string) =>
      ["vault", "localOrgs", userId, scope] as const,
    /** Mis roles por organización (tabla member local). */
    myOrgRoles: (userId: string | undefined) => ["vault", "myOrgRoles", userId] as const,
    /** Nombres/emails locales de usuarios (tabla user sync) para mostrar. */
    memberNames: (scope: string | undefined) => ["vault", "memberNames", scope] as const,
  },
  todos: {
    all: ["todos"] as const,
    list: <T extends object>(options: T) => ["todos", "list", options] as const,
    /** Variante paginada de `list` (límite incremental de la vista). */
    listPaged: <T extends object>(options: T, limit: number) =>
      ["todos", "list", options, limit] as const,
    /** Conteo de pendientes de una lista (badge). Derivada de `list` → se invalida con su prefijo. */
    listCount: <T extends object>(options: T) => ["todos", "list", options, "count"] as const,
    /**
     * Detalle de una tarea. Clave AGNÓSTICA de cuenta: el scoping por cuenta lo
     * inyecta de forma transparente `createAccountPowerSyncQuery` (prefijo
     * `["acct", userId, …]`). NO añadir `userId` aquí — usa el wrapper.
     */
    detail: (id: string | undefined) => ["todos", "detail", id] as const,
    /** Resolución list_id de un todo para el redirect de /todo/:id. */
    detailRedirect: (id: string | undefined) => ["todos", "detail-redirect", id] as const,
    stats: (userId: string | undefined) => ["todos", "stats", userId] as const,
    remindersPool: () => ["todos", "reminders-pool"] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Feed local (activity_recent). Incluye userId: aísla caché entre cuentas. */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Historial local de una entidad concreta. */
    entity: (userId: string | undefined, entityType: string, entityId: string | undefined) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  trash: {
    all: ["trash"] as const,
    lists: (userId: string | undefined) => ["trash", "lists", userId] as const,
    todos: (userId: string | undefined) => ["trash", "todos", userId] as const,
  },
  search: {
    all: ["search"] as const,
    /** Resultados de búsqueda. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Stats de cola de subida (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** ¿Hay datos locales? (botón de limpieza en profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  lists: {
    all: ["lists"] as const,
    /** Organización (sharing) asociada a una lista. Scoped vía wrapper de cuenta. */
    organization: (listId: string | undefined) => ["lists", "organization", listId] as const,
    /** All lists with theme + group metadata for a user (sidebar / route). */
    customLists: (userId: string | undefined) => ["lists", "customLists", userId] as const,
    /** List groups for a user. */
    groups: (userId: string | undefined) => ["lists", "groups", userId] as const,
    /** Single list detail by id. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    detail: (listId: string | undefined) => ["lists", "detail", listId] as const,
    /** Pending todo count per list_id (GROUP BY). */
    todoCounts: (userId: string | undefined) => ["lists", "todoCounts", userId] as const,
    /** Count of lists owned by user (profile stats). */
    count: (userId: string | undefined) => ["lists", "count", userId] as const,
    withCounts: (userId: string | undefined, limit?: number) =>
      ["lists", "withCounts", userId, limit] as const,
  },
  myDay: {
    all: ["myDay"] as const,
    /** Tareas de "Mi día". Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    todos: (dateKey: string) => ["myDay", "todos", dateKey] as const,
    /** Sugerencias de "Mi día". Scoped por cuenta vía wrapper. */
    suggestions: (dateKey: string) => ["myDay", "suggestions", dateKey] as const,
  },
  smartViews: {
    counts: (userId: string | undefined) => ["smartViews", "counts", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Catálogo completo de temas (getThemesQuery sin paginar; profile + list-dialog COMPARTEN caché). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  subtasks: {
    all: ["subtasks"] as const,
    /** Subtareas de una tarea. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    byTodo: (todoId: string | undefined) => ["subtasks", todoId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Adjuntos de una tarea. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    byTodo: (todoId: string) => ["attachments", todoId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
